'use client';
import { forwardRef } from 'react';
import { Switch as HeadlessSwitch } from '@headlessui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const switchVariants = cva(
  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'focus:ring-offset-white',
        error: 'focus:ring-red-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const switchThumbVariants = cva(
  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
  {
    variants: {
      checked: {
        true: 'translate-x-6',
        false: 'translate-x-1',
      },
    },
    defaultVariants: {
      checked: false,
    },
  }
);

export interface SwitchProps extends VariantProps<typeof switchVariants> {
  className?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, variant, checked, ...props }, ref) => {
    return (
      <HeadlessSwitch
        ref={ref}
        checked={checked}
        className={cn(
          switchVariants({ variant, className }),
          checked ? 'bg-brand-teal' : 'bg-gray-200'
        )}
        {...props}
      >
        <span className='sr-only'>Enable notifications</span>
        <span
          className={cn(
            switchThumbVariants({ checked: checked ? true : false })
          )}
        />
      </HeadlessSwitch>
    );
  }
);
Switch.displayName = 'Switch';

export { Switch };
